import React from 'react';
import DoubleFilesIcon from '../../../../assets/icons/double-files-purple-bg.svg';
import HandleIcon from '../../../../assets/icons/edit-row-script.svg';
import BlueBagIcon from '../../../../assets/icons/blue-bag.svg';
import { useTranslation } from 'react-i18next';
import './OrderScriptRow.less';

export type OrderScriptRowProps = {
  id: string;
  title: string;
  message: string;
  price?: number | null;
  isJiggling?: boolean;
  onLongPress?: () => void;
  onStartDrag?: (id: string, startClientY: number) => void;
  setRowRef?: (id: string, el: HTMLDivElement | null) => void;
  isDragging?: boolean;
  dragStyle?: React.CSSProperties;
};

const OrderScriptRow: React.FC<OrderScriptRowProps> = ({ id, title, message, price, isJiggling, onLongPress, onStartDrag, setRowRef, isDragging, dragStyle }) => {
  const { t } = useTranslation();
  const isFree = !price || price <= 0;
  const pressTimer = React.useRef<number | null>(null);
  const lastPointerY = React.useRef<number | null>(null);
  const rowRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    setRowRef?.(id, rowRef.current);
    return () => setRowRef?.(id, null);
  }, [id, setRowRef]);

  const startPressTimer = (e?: React.PointerEvent | React.MouseEvent | React.TouchEvent) => {
    e?.preventDefault?.();
    // @ts-expect-error unifying events
    const clientY = (e?.clientY ?? (e?.touches && e.touches[0]?.clientY)) as number | undefined;
    if (typeof clientY === 'number') lastPointerY.current = clientY;
    if (pressTimer.current) {
      window.clearTimeout(pressTimer.current);
    }
    const delay = isJiggling ? 300 : 2000;
    pressTimer.current = window.setTimeout(() => {
      if (!isJiggling) {
        onLongPress?.();
      }
      if (lastPointerY.current != null) {
        onStartDrag?.(id, lastPointerY.current);
      }
    }, delay);
  };

  const cancelPressTimer = (e?: React.PointerEvent | React.MouseEvent | React.TouchEvent) => {
    e?.preventDefault?.();
    if (pressTimer.current) {
      window.clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
  };
  return (
    <div ref={rowRef} className={`order-script-row${isJiggling ? ' jiggling' : ''}${isDragging ? ' dragging' : ''}`} role="listitem" style={isDragging ? dragStyle : undefined}>
      <div className="order-script-row-left-icon">
        <img src={DoubleFilesIcon} alt="type" draggable={false} />
      </div>

      <div className="order-script-row-content">
        <div className="order-script-row-title" title={title}>
          {title}
        </div>
        <div className="order-script-row-preview" title={message}>
          {message}
        </div>
      </div>

      <div className="order-script-row-price-zone" aria-label="price">
        <img className="order-script-row-bag" src={BlueBagIcon} alt="price" draggable={false} />
        <span className="order-script-row-price">{isFree ? t('free') : `${price!.toFixed(2)} €`}</span>
      </div>

      <button
        type="button"
        className="order-script-row-handle"
        aria-label="reorder"
        onPointerDown={startPressTimer}
        onPointerUp={cancelPressTimer}
        onPointerLeave={cancelPressTimer}
        onPointerCancel={cancelPressTimer}
        onContextMenu={(e) => e.preventDefault()}
      >
        <img src={HandleIcon} alt="reorder" draggable={false} />
      </button>
    </div>
  );
};

export default OrderScriptRow;
